import Vue from 'vue'

export const state = () => ({
  clients: [],
  salesmen: [],
  coordinators: [],
  allSalesmen: [],
  periodHistoric: [],
  isOpen: false,
  permission: null,
  loadingClients: false,
  openPeriodId: null,
  addressIndexes: {},
  noVisitReasons: [],
  lastFetch: null,
})

const aditionalFields = client => ({
  ...client,
  client: {
    ...(client.client || {}),
    contact: client.client?.contact || client.client?.buyer || '',
    phone: client.client?.phone || '',
    phone2: client.client?.phone2 || '',
  },
  transferEmployeeApplyDate: '',
  transferEmployeeEndDate: '',
  observation: client.client?.observation ? client.client.observation : '',
  removeReason: '',
  noVisitReason: '',
  transferClient: {
    comercialDivision: client.client?.comercialDivision,
    regionalId: client.client?.regionalId,
    areaId: client.client?.areaId,
    areaDescription: client.client?.areaDescription ?? '',
  },
})

const mapSalesman = salesman => ({
  ...salesman,
  areaDescription: salesman.areaDescription || [],
})

const setAllSalesmen = (salesmen = [], user) => {
  const removeEmployee = {
    employeeCode: -1,
    name: 'INATIVAR CLIENTE',
    jobTitle: 'Vendedor',
    areaDescription: [],
  }
  const telesaleEmployee = {
    employeeCode: 0,
    name: 'TELEVENDAS',
    jobTitle: 'Vendedor',
    areaDescription: [],
  }
  if (user.jobTitle !== 'Vendedor') salesmen.unshift(removeEmployee)
  salesmen.unshift(telesaleEmployee)
  return salesmen
}

export const mutations = {
  UPDATE_ROOT: (state, payload) => Object.assign(state, payload),
  setState: (state, value) => Object.assign(state, value),
  setClient: (state, value) => Vue.set(state.clients, value.id, value),
  setMultipleClients: (state, values) => {
    state.clients = state.clients.map(client => {
      if (
        values.has(`${client.client.code}-${client.client.comercialDivision}`)
      )
        return {
          ...values.get(
            `${client.client.code}-${client.client.comercialDivision}`
          ),
        }
      return { ...client }
    })
  },
  resetClientTransferToDate: (state, value) =>
    (state.clients = state.clients.map(client => ({
      ...client,
      toDate: null,
    }))),
  applyDateToMany: (state, value) => {
    state.clients = state.clients.map(client => {
      if (value.ids.includes(client.id) && client.visitDay !== value.date.new) {
        return {
          ...client,
          toDate: value.date.new,
        }
      }
      if (client.toDate === value.date.old) {
        return {
          ...client,
          toDate: null,
        }
      }
      return client
    })
  },
  resetDateToMany: (state, value) => {
    state.clients = state.clients.map(client => {
      if (value.ids.includes(client.id) && client.toDate === value.date) {
        return {
          ...client,
          toDate: null,
        }
      }
      return client
    })
  },
  setPeriodHistoric: (state, value) => {
    state.periodHistoric = value
  },
  setPermissions: (state, value) => {
    state.permission = value.permission
  },
  setLoadingClients: (state, value) => (state.loadingClients = value),
}

export const actions = {
  async fetch({ commit, rootState, state, dispatch }) {
    const now = Date.now()
    const THIRTY_MINUTES = 30 * 60 * 1000
    if (state.lastFetch && now - state.lastFetch < THIRTY_MINUTES) return

    commit('setLoadingClients', true)
    const { data } = await this.$axios.get('/v3/app/transfer/forms')
    const tokenize = (v, id) => ({ ...v, id })

    const user = {
      employeeCode: rootState.auth.user.employeeCode,
      name: rootState.auth.user.name,
      teleSales: rootState.auth.user.teleSales,
      ancestors: rootState.auth.user.ancestors,
      jobTitle: rootState.auth.user.jobTitle,
    }
    if (user.jobTitle !== 'Vendedor') data.salesmen?.unshift(user)

    commit('setState', {
      clients: data.clients.map?.(aditionalFields).map?.(tokenize) ?? [],
      salesmen: data.salesmen.map?.(mapSalesman).map?.(tokenize) ?? [],
      coordinators: data.coordinators.map?.(mapSalesman).map?.(tokenize) ?? [],
      allSalesmen:
        setAllSalesmen(data.allSalesmen, rootState.auth.user)
          .map?.(mapSalesman)
          .map?.(tokenize) ?? [],
      periodHistoric: data.historic.map?.(tokenize) ?? [],
      openPeriodId: data.openPeriodId,
      noVisitReasons: data.noVisitReasons,
      lastFetch: now,
    })
    console.log('loaded state', state)
    dispatch('updateAddressIndexes', state.clients)
    commit('setLoadingClients', false)
  },
  async updatePeriodHistoricList({ state, commit }, salesmanCode = null) {
    if (!state.openPeriodId) return
    const params = salesmanCode ? { salesmanCode } : {}
    const {
      data,
    } = await this.$axios.get(
      `/v3/app/transfer/period/historic/${state.openPeriodId}`,
      { params }
    )
    commit('setPeriodHistoric', data)
  },
  async getPermissions({ commit }) {
    const { data } = await this.$axios.get('/v3/app/transfer/settings')
    commit('setPermissions', data)
  },
  async updateAddressIndexes({ commit }, data) {
    const ufs = new Set()
    const cities = new Set()
    const districts = new Set()

    const ufToCities = {}
    const ufToDistricts = {}
    const cityToDistricts = {}

    for (const item of data) {
      if (!item.client?.address) continue

      const uf = item.client.address.uf?.trim() ?? null
      const city = item.client.address.city?.trim() ?? null
      const district = item.client.address.district?.trim() ?? null

      if (uf) {
        ufs.add(uf)

        if (!ufToCities[uf]) ufToCities[uf] = new Set()
        if (city) ufToCities[uf].add(city)

        if (!ufToDistricts[uf]) ufToDistricts[uf] = new Set()
        if (district) ufToDistricts[uf].add(district)
      }

      if (city) {
        cities.add(city)

        if (!cityToDistricts[city]) cityToDistricts[city] = new Set()
        if (district) cityToDistricts[city].add(district)
      }

      if (district) districts.add(district)
    }

    const addressIndexes = {
      uf: Array.from(ufs).sort((a, b) => (a > b ? 1 : -1)),
      city: Array.from(cities).sort((a, b) => (a > b ? 1 : -1)),
      district: Array.from(districts).sort((a, b) => (a > b ? 1 : -1)),
      'uf-city': Object.fromEntries(
        Object.entries(ufToCities).map(([key, value]) => [
          key,
          Array.from(value).sort((a, b) => (a > b ? 1 : -1)),
        ])
      ),
      'uf-district': Object.fromEntries(
        Object.entries(ufToDistricts).map(([key, value]) => [
          key,
          Array.from(value).sort((a, b) => (a > b ? 1 : -1)),
        ])
      ),
      'city-district': Object.fromEntries(
        Object.entries(cityToDistricts).map(([key, value]) => [
          key,
          Array.from(value).sort((a, b) => (a > b ? 1 : -1)),
        ])
      ),
    }
    commit('UPDATE_ROOT', { addressIndexes })
  },
  async saveBulkContacts({ commit }, selectedClients) {
    if (!selectedClients || selectedClients.length === 0) return
    const payload = selectedClients.map(item => ({
      code: item.client.code,
      comercialDivision: item.client.comercialDivision,
      contact: item.client.contact,
      phone: item.client.phone,
      phone2: item.client.phone2,
      category: item.client.category,
    }))

    try {
      console.log('Enviando contatos para o banco:', payload)
      await this.$axios.post('/v3/app/portfolio/update-clients', {
        clients: payload,
      })
      return true
    } catch (error) {
      console.error('Erro ao salvar contatos em lote:', error)
      throw error
    }
  },
}
